/**
 * How one question-and-answer is displayed.
 *
 * Split from App.tsx, which is the shell — the conversation state, the event stream, the
 * output window. This file is what a single exchange looks like on the way past: the
 * working indicator while retrieval runs, the prose with its references made clickable,
 * the scope line naming which versions an answer read, and the failure
 * notice with a retry offered only for the kinds worth retrying.
 *
 * The types live here rather than in App.tsx because this is the module about exchanges.
 * App builds them from the event stream; everything that decides how one *reads* is here.
 *
 * Two rules from the product survive in this file and must not be tidied away. A retry is
 * offered by failure KIND, never by status — the daily cap and the rate limiter are both
 * 429 and mean opposite things. And attribution belongs in the sentence rather than in
 * badge clutter, which is why the scope line is one line of prose and not a row of chips.
 */

import type { JSX } from 'react'

import { useEffect, useState } from 'react'
import { siteBootstrap } from './account'
import type { AnswerSummary, FailureKind } from './api'
import type { Card } from './cards'
import { t, type StringKey } from './i18n'
import { ModeBadge, RESEARCH } from './mode'
import { ACCOUNT_URL, SIGN_UP_URL } from './origins'


export interface Failure {
  message: string
  kind: FailureKind
}

export interface Exchange {
  question: string
  prose: string
  summary: AnswerSummary | null
  retrieving: string | null
  failure: Failure | null
}


/**
 * Is this exchange still in flight?
 *
 * Derived rather than stored, so it cannot drift out of step with the events
 * that actually arrived. In flight means exactly "no terminal event yet": the
 * relay ends every request with `done` or `error`, and `streamAnswer`'s final
 * pass invents one when the stream simply stops.
 *
 * It deliberately does NOT ask whether prose has started. It used to, and that
 * cost two things. The reader lost the indicator the moment the first token
 * landed — leaving the longest stretch of the request, the model writing,
 * with nothing on screen to distinguish "still going" from "finished". And a
 * stream that broke mid-sentence slipped past the truncation guard in
 * `streamAnswer`, because prose already existed, so a half-written answer was
 * left looking complete: no error, no scope line, nothing to say it had been
 * cut off. A question this shape should be about the events, not about how
 * much text happens to have arrived.
 */
export function isWorking(exchange: Exchange): boolean {
  return exchange.failure === null && exchange.summary === null
}

/**
 * A tool name as something a reader recognises.
 *
 * The relay reports which tool is running; showing `get_passage` would be
 * leaking an implementation detail, and showing one constant string for every
 * retrieval makes three sequential lookups look like one frozen line.
 */
function toolLabelKey(tool: string): StringKey {
  const key = `tool.${tool}` as StringKey
  return key in TOOL_LABELS ? key : 'chat.retrieving'
}

const TOOL_LABELS: Record<string, true> = {
  'tool.get_passage': true,
  'tool.search': true,
  'tool.compare_translations': true,
  'tool.lexicon': true,
  'tool.words_in_verse': true,
  'tool.cross_references': true,
}

export function ExchangeView({
  exchange,
  onOpen,
  onRetry,
}: {
  exchange: Exchange
  onOpen: (card: Card) => void
  onRetry?: () => void
}): JSX.Element {
  return (
    <section className="exchange">
      <p className="question">{exchange.question}</p>

      {/* Above the prose, because it changes how the prose should be read, and
          a reader who has already read it has been told too late. It renders
          from the summary, so it appears when the answer completes — which is
          also when the server has said what mode actually produced it. */}
      <ModeBadge mode={exchange.summary?.mode} />

      {exchange.prose && (
        <div
          className={
            exchange.summary?.mode === RESEARCH ? 'answer answer--research' : 'answer'
          }
        >
          <Prose text={exchange.prose} onOpen={onOpen} />
        </div>
      )}

      {/* Below the answer, not above it, and up for the WHOLE request.
          It used to disappear the moment the first token arrived, which left
          the longest phase — the model writing — with no indicator at all, and
          made a finished answer and one still being written look identical.
          Trailing the prose also means the reader's eye is already there. */}
      {isWorking(exchange) && (
        <p className="progress" aria-live="polite">
          {exchange.retrieving
            ? t(toolLabelKey(exchange.retrieving))
            : exchange.prose
              ? t('chat.writing')
              : t('chat.thinking')}
          {/* A blinking cursor rather than a spinner: this is a text product,
              and the mark that says "still writing" should look like writing.
              aria-hidden because the line's own text already says it, and a
              screen reader announcing a decoration would be noise. */}
          <span className="progress__cursor" aria-hidden="true" />
        </p>
      )}

      {exchange.failure && <FailureNotice failure={exchange.failure} onRetry={onRetry} />}

      {exchange.summary && (
        <ScopeLine
          question={exchange.question}
          prose={exchange.prose}
          summary={exchange.summary}
          onOpen={onOpen}
        />
      )}
    </section>
  )
}

/**
 * A failure the reader cannot miss.
 *
 * The previous version was one line of small red text, which is how a broken
 * deployment went unnoticed for an evening. This is a bordered block carrying
 * role="alert" so assistive technology announces it.
 *
 * A failed question no longer spends an allowance, so retrying costs nothing —
 * and the case that looks least worth retrying, a misconfigured server, is
 * precisely the one a reader comes back to once someone has fixed it. The two
 * that stay silent are the ones where the same request genuinely cannot
 * succeed: the daily cap, which is over until midnight UTC, and a rejected
 * request, which needs rewriting rather than resending.
 */
// standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
// Python constant, so the single home stays the server and this mirrors it. The
// paydown is generating the vocabulary into TypeScript rather than carrying a marker
// per site -- see docs/engineering-standards.md.
const RETRYABLE: readonly FailureKind[] = ['unconfigured', 'provider', 'internal', 'rate_limited']

/** Today's free questions spent and the wallet empty: the notice links to the account page. */
// standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
// Python constant, so the single home stays the server and this mirrors it. The
// paydown is generating the vocabulary into TypeScript rather than carrying a marker
// per site -- see docs/engineering-standards.md.
const PAYMENT_REQUIRED: FailureKind = 'payment_required'

/** The free allowance spent. */
// standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
// Python constant, so the single home stays the server and this mirrors it -- see docs/engineering-standards.md.
const CAP_REACHED: FailureKind = 'cap_reached'

function FailureNotice({ failure, onRetry }: { failure: Failure; onRetry?: () => void }) {
  return (
    <div className="failure" role="alert">
      <p className="failure__message">{failureMessage(failure)}</p>
      {/* The server's sentence names both alternatives; this is the door to them. */}
      {failure.kind === PAYMENT_REQUIRED && (
        <a className="link" href={ACCOUNT_URL}>
          {t('chat.openAccount')}
        </a>
      )}
      {failure.kind === CAP_REACHED && <SignUpOffer />}
      {RETRYABLE.includes(failure.kind) && onRetry && (
        <button type="button" className="link" onClick={onRetry}>
          {t('chat.retry')}
        </button>
      )}
    </div>
  )
}

/**
 * Under the cap notice: an account, when one genuinely gives more questions.
 *
 * Asked of the server rather than assumed, because the anonymous allowance and
 * an account's are configured separately — a deployment may give both the same,
 * and then offering an account here would promise something it does not give.
 * Nothing renders for a signed-in reader, while sign-up is closed, or until the
 * answer is in. A new tab, so the conversation on this page survives.
 */
function SignUpOffer(): JSX.Element | null {
  const [perDay, setPerDay] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    void siteBootstrap().then((facts) => {
      const free = facts.freeQuestionsPerDay
      const withAccount = facts.signupQuestionsPerDay
      const isWorthOffering = facts.account === null && free !== null && withAccount !== null && withAccount > free
      if (!cancelled && isWorthOffering) setPerDay(withAccount)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (perDay === null) return null
  return (
    <p className="muted">
      {t('chat.capSignUpOffer', { count: perDay })}{' '}
      <a href={SIGN_UP_URL} target="_blank" rel="noopener">
        {t('account.signUp')}
      </a>
    </p>
  )
}

/**
 * Prefer our own wording where the reader needs more than the server said.
 * The cap and the rate limit both arrive as a terse 429; only the client knows
 * how to say what each means for the person reading it.
 */
function failureMessage(failure: Failure): string {
  if (failure.kind === CAP_REACHED) return t('chat.capReached')
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  if (failure.kind === 'rate_limited') return t('chat.rateLimited')
  return failure.message
}

/**
 * The quiet scope line under an answer, and the way into the evidence view.
 *
 * Read-only, and never changes: the record of what this answer read. The versions are
 * changed in the row above the question box (`VersionRow`), never here.
 *
 * The record is what the tools actually returned (`read_versions`), not the set the ask
 * sent: the row always holds the Greek, and an answer about Genesis read none of it. An
 * answer that read nothing through a tool falls back to the versions its claims name.
 */
function ScopeLine({
  question,
  prose,
  summary,
  onOpen,
}: {
  question: string
  prose: string
  summary: AnswerSummary
  onOpen: (card: Card) => void
}) {
  const read = summary.read_versions ?? []
  const codes = read.length > 0 ? read : summary.consulted_versions
  const notes = summary.active_notes ?? []
  return (
    <>
      <div className="scope">
        {codes.length > 0 && (
          <span>
            {t('chat.groundedIn')}: {codes.join(', ')}
          </span>
        )}
        {summary.crossed_a_border && <span className="scope__border">{t('chat.beyondText')}</span>}
        <button
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'evidence', question, prose, summary })}
        >
          {t('chat.evidence')}
        </button>
      </div>
      {notes.length > 0 && (
        <div className="scope__notes" role="note" aria-label={t('chat.activeNote')}>
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      )}
    </>
  )
}

// Scripture references inside the prose, turned into things you can click.
// Every citation being live is the product's central claim: the answer survives
// the click, and a competitor's does not.
//
// The trailing `(?::\d+)?` is what makes a cross-chapter range whole. Without it
// "Job 38:1–41:34" matched only "Job 38:1–41" — not a truncated link but a
// *different passage*, since that parses as verses 1–41 of chapter 38, so the
// card opened text the answer never cited. "Matt 5:1-7:29" failed the same way.
// Any change here needs the same range forms as refparse's scan pattern, which
// is the authoritative one; this is its display-side twin and the two have to
// agree on what counts as a reference.
const REFERENCE_PATTERN =
  /\b(?:[123]\s)?[A-Z][a-z]+(?:\sof\s[A-Z][a-z]+)?\s\d+:\d+(?:\s*[-–]\s*\d+(?::\d+)?)?/g

function Prose({ text, onOpen }: { text: string; onOpen: (card: Card) => void }) {
  const parts: React.ReactNode[] = []
  let cursor = 0

  for (const match of text.matchAll(REFERENCE_PATTERN)) {
    const start = match.index ?? 0
    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(
      <button
        key={`${start}-${match[0]}`}
        type="button"
        className="citation"
        onClick={() => onOpen({ kind: 'passage', reference: match[0] })}
      >
        {match[0]}
      </button>,
    )
    cursor = start + match[0].length
  }
  if (cursor < text.length) parts.push(text.slice(cursor))

  return <>{parts}</>
}

